/**
 * Single source of truth for calling the Cloudflare Worker API from the frontend.
 * Use getWorkerApiBase() or postWorkerApi/getWorkerApi for all worker endpoints.
 */

/** Base URL for worker API: dev (Wrangler) or same origin in production. */
export function getWorkerApiBase(): string {
  return import.meta.env.DEV ? 'http://localhost:8787' : '';
}

export type WorkerApiSuccess<T> = { data: T; ok: true; status: number };
export type WorkerApiError = { ok: false; status: number; error?: string };

/**
 * POST to a worker API path with JSON body. Returns { data, ok: true } or { ok: false, status, error }.
 * Does not throw; callers handle ok and show toast or report as needed.
 */
export async function postWorkerApi<T = unknown>(
  path: string,
  body: unknown
): Promise<WorkerApiSuccess<T> | WorkerApiError> {
  const base = getWorkerApiBase();
  const res = await fetch(`${base}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  let data: T;
  try {
    data = (await res.json()) as T;
  } catch {
    return { ok: false, status: res.status, error: await res.text().catch(() => 'Invalid response') };
  }
  if (!res.ok) {
    const error = data && typeof data === 'object' && 'error' in data && typeof (data as { error?: string }).error === 'string'
      ? (data as { error: string }).error
      : res.statusText;
    return { ok: false, status: res.status, error };
  }
  return { data, ok: true, status: res.status };
}

/**
 * GET a worker API path. Returns { data, ok: true } or { ok: false, status, error }.
 */
export async function getWorkerApi<T = unknown>(path: string): Promise<WorkerApiSuccess<T> | WorkerApiError> {
  const base = getWorkerApiBase();
  const res = await fetch(`${base}${path}`);
  let data: T;
  try {
    data = (await res.json()) as T;
  } catch {
    return { ok: false, status: res.status, error: await res.text().catch(() => 'Invalid response') };
  }
  if (!res.ok) {
    const error = data && typeof data === 'object' && 'error' in data && typeof (data as { error?: string }).error === 'string'
      ? (data as { error: string }).error
      : res.statusText;
    return { ok: false, status: res.status, error };
  }
  return { data, ok: true, status: res.status };
}

/**
 * GET with Supabase Auth Bearer token (for endpoints that require the lead, e.g. quotation-request by id when owned).
 */
export async function getWorkerApiWithAuth<T = unknown>(
  path: string,
  getSession: () => Promise<{ data: { session: { access_token: string } | null } }>
): Promise<WorkerApiSuccess<T> | WorkerApiError> {
  const { data: sessionData } = await getSession();
  const token = sessionData?.session?.access_token;
  if (!token) return { ok: false, status: 401, error: 'Debes iniciar sesión' };
  const base = getWorkerApiBase();
  const res = await fetch(`${base}${path}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  let data: T;
  try {
    data = (await res.json()) as T;
  } catch {
    return { ok: false, status: res.status, error: await res.text().catch(() => 'Invalid response') };
  }
  if (!res.ok) {
    const error = data && typeof data === 'object' && 'error' in data && typeof (data as { error?: string }).error === 'string'
      ? (data as { error: string }).error
      : res.statusText;
    return { ok: false, status: res.status, error };
  }
  return { data, ok: true, status: res.status };
}

/**
 * POST with Supabase Auth Bearer token (for endpoints that require the lead user, e.g. respond).
 * Pass getSession from supabase.auth.getSession; access_token is added to Authorization.
 */
export async function postWorkerApiWithAuth<T = unknown>(
  path: string,
  body: unknown,
  getSession: () => Promise<{ data: { session: { access_token: string } | null } }>
): Promise<WorkerApiSuccess<T> | WorkerApiError> {
  const { data: sessionData } = await getSession();
  const token = sessionData?.session?.access_token;
  if (!token) return { ok: false, status: 401, error: 'Debes iniciar sesión' };
  const base = getWorkerApiBase();
  const res = await fetch(`${base}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  let data: T;
  try {
    data = (await res.json()) as T;
  } catch {
    return { ok: false, status: res.status, error: await res.text().catch(() => 'Invalid response') };
  }
  if (!res.ok) {
    const error = data && typeof data === 'object' && 'error' in data && typeof (data as { error?: string }).error === 'string'
      ? (data as { error: string }).error
      : res.statusText;
    return { ok: false, status: res.status, error };
  }
  return { data, ok: true, status: res.status };
}

/**
 * POST with auth using FormData (e.g. file upload to /api/quotation-request/upload-document).
 * Do not set Content-Type so the browser sets multipart/form-data with boundary.
 */
export async function postWorkerApiWithAuthFormData<T = unknown>(
  path: string,
  formData: FormData,
  getSession: () => Promise<{ data: { session: { access_token: string } | null } }>
): Promise<WorkerApiSuccess<T> | WorkerApiError> {
  const { data: sessionData } = await getSession();
  const token = sessionData?.session?.access_token;
  if (!token) return { ok: false, status: 401, error: 'Debes iniciar sesión' };
  const base = getWorkerApiBase();
  const res = await fetch(`${base}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  let data: T;
  try {
    data = (await res.json()) as T;
  } catch {
    return { ok: false, status: res.status, error: await res.text().catch(() => 'Invalid response') };
  }
  if (!res.ok) {
    const error = data && typeof data === 'object' && 'error' in data && typeof (data as { error?: string }).error === 'string'
      ? (data as { error: string }).error
      : res.statusText;
    return { ok: false, status: res.status, error };
  }
  return { data, ok: true, status: res.status };
}
